/**
 * Standardized Error Handling System
 * 
 * Provides consistent error handling across all platforms
 */

import { BackendApiError, ApiValidationError } from './backend-response-types';

// =============================================================================
// Standard Error Types
// =============================================================================

export enum ErrorType {
  NETWORK_ERROR = 'NETWORK_ERROR',
  API_ERROR = 'API_ERROR', 
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  AUTHENTICATION_ERROR = 'AUTHENTICATION_ERROR',
  AUTHORIZATION_ERROR = 'AUTHORIZATION_ERROR',
  NOT_FOUND_ERROR = 'NOT_FOUND_ERROR',
  SERVER_ERROR = 'SERVER_ERROR',
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

export interface StandardError {
  type: ErrorType;
  message: string;
  code?: string;
  details?: Record<string, any>;
  originalError?: Error;
}

// =============================================================================
// Error Classification
// =============================================================================

export function classifyError(error: any): StandardError {
  // Network errors
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return {
      type: ErrorType.NETWORK_ERROR,
      message: 'Network connection failed. Please check your internet connection.',
      originalError: error,
    };
  }
  
  // Backend API errors
  if (error instanceof BackendApiError) {
    return {
      type: getErrorTypeFromCode(error.code),
      message: error.message,
      code: error.code,
      details: error.details,
      originalError: error,
    };
  }
  
  // Validation errors
  if (error instanceof ApiValidationError) {
    return {
      type: ErrorType.VALIDATION_ERROR,
      message: error.message,
      details: error.validationErrors,
      originalError: error,
    };
  }
  
  // HTTP errors
  if (error.status) {
    return classifyHttpError(error.status, error.message || 'HTTP Error');
  }
  
  // Default fallback
  return {
    type: ErrorType.UNKNOWN_ERROR,
    message: error.message || 'An unexpected error occurred',
    originalError: error,
  };
}

function getErrorTypeFromCode(code: string): ErrorType {
  switch (code) {
    case 'UNAUTHORIZED':
    case 'INVALID_TOKEN':
      return ErrorType.AUTHENTICATION_ERROR;
      
    case 'FORBIDDEN':
    case 'INSUFFICIENT_PERMISSIONS':
      return ErrorType.AUTHORIZATION_ERROR;
      
    case 'NOT_FOUND':
    case 'RESOURCE_NOT_FOUND':
      return ErrorType.NOT_FOUND_ERROR;
      
    case 'VALIDATION_FAILED':
    case 'INVALID_REQUEST':
      return ErrorType.VALIDATION_ERROR;
      
    case 'SERVER_ERROR':
    case 'INTERNAL_ERROR':
      return ErrorType.SERVER_ERROR;
      
    case 'TIMEOUT':
      return ErrorType.TIMEOUT_ERROR;
      
    default:
      return ErrorType.API_ERROR;
  }
}

function classifyHttpError(status: number, message: string): StandardError {
  if (status === 401) {
    return {
      type: ErrorType.AUTHENTICATION_ERROR,
      message: 'Authentication required. Please log in.',
      code: 'UNAUTHORIZED',
    };
  }
  
  if (status === 403) {
    return {
      type: ErrorType.AUTHORIZATION_ERROR,
      message: 'You do not have permission to perform this action.',
      code: 'FORBIDDEN',
    };
  }
  
  if (status === 404) {
    return {
      type: ErrorType.NOT_FOUND_ERROR,
      message: 'The requested resource was not found.',
      code: 'NOT_FOUND',
    };
  }
  
  if (status >= 400 && status < 500) {
    return {
      type: ErrorType.VALIDATION_ERROR,
      message: message || 'Invalid request',
      code: `HTTP_${status}`,
    };
  }
  
  if (status >= 500) {
    return {
      type: ErrorType.SERVER_ERROR,
      message: 'Server error occurred. Please try again later.',
      code: `HTTP_${status}`,
    };
  }
  
  return {
    type: ErrorType.UNKNOWN_ERROR,
    message: message || `HTTP error ${status}`,
    code: `HTTP_${status}`,
  };
}

// =============================================================================
// User-Friendly Error Messages
// =============================================================================

export function getUserFriendlyMessage(error: StandardError): string {
  switch (error.type) {
    case ErrorType.NETWORK_ERROR:
      return 'Unable to connect to the server. Please check your internet connection and try again.';
      
    case ErrorType.AUTHENTICATION_ERROR:
      return 'Please log in to continue.';
      
    case ErrorType.AUTHORIZATION_ERROR:
      return 'You do not have permission to perform this action.';
      
    case ErrorType.NOT_FOUND_ERROR:
      return 'The requested item was not found.';
      
    case ErrorType.VALIDATION_ERROR:
      return error.message || 'Please check your input and try again.';
      
    case ErrorType.SERVER_ERROR:
      return 'A server error occurred. Please try again in a moment.';
      
    case ErrorType.TIMEOUT_ERROR:
      return 'The request timed out. Please try again.';
      
    default:
      return error.message || 'An unexpected error occurred. Please try again.';
  }
}

// =============================================================================
// Retry Logic
// =============================================================================

export interface RetryConfig {
  maxRetries: number;
  retryDelay: number;
  retryableErrors: ErrorType[];
}

export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  retryDelay: 1000,
  retryableErrors: [
    ErrorType.NETWORK_ERROR,
    ErrorType.TIMEOUT_ERROR,
    ErrorType.SERVER_ERROR,
  ],
};

export function shouldRetry(error: StandardError, config = DEFAULT_RETRY_CONFIG): boolean {
  return config.retryableErrors.includes(error.type);
}

export async function withRetry<T>(
  operation: () => Promise<T>,
  config = DEFAULT_RETRY_CONFIG
): Promise<T> {
  let lastError: StandardError;
  
  for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = classifyError(error);
      
      if (attempt === config.maxRetries || !shouldRetry(lastError, config)) {
        throw lastError;
      }
      
      await new Promise(resolve => 
        setTimeout(resolve, config.retryDelay * Math.pow(2, attempt))
      );
    }
  }
  
  throw lastError!;
}

// =============================================================================
// Logging Integration
// =============================================================================

export interface ErrorLogger {
  logError(error: StandardError, context?: Record<string, any>): void;
}

export class ConsoleErrorLogger implements ErrorLogger {
  logError(error: StandardError, context?: Record<string, any>): void {
    console.error('API Error:', {
      type: error.type,
      message: error.message,
      code: error.code,
      details: error.details,
      context,
      stack: error.originalError?.stack,
    });
  }
}

export const defaultErrorLogger = new ConsoleErrorLogger();